import { StripeNotificationOk } from '@type/v1-api-types';
import { useState, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { Button, Anchor } from '@components/common';
import { useTranslation } from 'next-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';
import { setMenuTopOffset } from '@components/layout/Header/MainNavbar/config/helper-functions';


// Interface
// ****************************************
interface SalesCampaignStripeProps {
  data?: StripeNotificationOk,
  hasCloseButton?: boolean,
}


/**
 * @description SalesCampaignStripe component for displaying the sales campaign stripe above the navbar.
 *
 * * **data** is the data from the API. Don't display the stripe if there's no data.
 * * **hasCloseButton** - boolean: show/hide the close button
 */
export const SalesCampaignStripe = (props: SalesCampaignStripeProps) => {
  // Props destructuring
  const {
    data,
    hasCloseButton = true,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // State for showing/closing the stripe
  const [showStripe, setShowStripe] = useState(true);

  // Close the stripe. It will trigger the hide transition.
  const closeStripe = () => {
    setShowStripe(false);
    // recalculate the offset css variable and account for the transition duration (duration-300)
    setTimeout(() => {
      setMenuTopOffset();
    }, 300);
  };

  // Save the state in session storage so that it doesn't show up again.
  // Will do this after the transition finishes.
  const saveToSessionStorage = () => {
    sessionStorage.setItem('salesCampaignStripe', 'closed');
  };

  // If there's no data or the stripe was closed, don't display the stripe
  if (!data || sessionStorage.getItem('salesCampaignStripe') === 'closed') return null;


  // Render component
  // ****************************************
  return (
    <Transition
      as={Fragment}
      show={showStripe}
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      afterLeave={saveToSessionStorage}
    >
      <div className="z-2 bg-gradient-to-r from-secondary-dark to-secondary-light text-white">
        <div className="container flex flex-wrap items-center justify-between py-3 sm:flex-nowrap sm:gap-3 sm:py-1.5">

          {/* CTA text */}
          <p
            className="w-full grow pr-7 font-medium sm:w-auto sm:pr-2"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(data?.body) }}
          />

          { /* CTA button. We're not using LinkStyled because the target might be a symfony page */
            data?.url && data?.cta && (
              <Anchor
                href={data?.url}
                size="sm"
                color="white"
                rounding="full"
                styling="outline"
                className="mb-1 mt-3 !py-2 sm:my-0"
              >
                {data?.cta}
              </Anchor>
            )
          }

          { /* Close button */
            hasCloseButton && (
              <Button
                styling="none"
                className="absolute right-1 top-1 p-2 sm:relative sm:right-auto sm:top-auto sm:pr-0"
                aria-label={t('global.btn.close')}
                title={t('global.btn.close')}
                onClick={closeStripe}
              >
                <XMarkIcon className="size-4.5 stroke-[3px] text-white" />
              </Button>
            )
          }

        </div>
      </div>
    </Transition>
  );
};
